import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Zap, Gauge, Check, X, Shield, Clock, CircleDollarSign } from "lucide-react";

export function Hero({ t, onCta }) {
  const scrollToHow = (e) => {
    e.preventDefault();
    const el = document.getElementById("how");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="relative overflow-hidden" id="top">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-14 pb-10 sm:pb-16 lg:pt-20 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
        {/* LEFT — Copy + CTA */}
        <div className="lg:col-span-7 rise-in order-1 lg:order-1">
          <div className="flex items-center gap-2 mb-3 sm:mb-5">
            <span className="tag tag-brand" data-testid="hero-overline">
              <Zap className="w-3.5 h-3.5" /> {t.hero.overline}
            </span>
          </div>

          <h1 className="font-heading font-extrabold text-[34px] sm:text-5xl lg:text-7xl leading-[1.05] text-[#292524] mb-4 sm:mb-6">
            {t.hero.h1_a}{" "}
            <span className="text-[#D95D39]">{t.hero.h1_b}</span>
          </h1>

          <p className="text-base sm:text-lg lg:text-xl text-[#57534E] leading-relaxed max-w-2xl mb-5 sm:mb-8">
            {t.hero.sub}
          </p>

          {/* CTA — Full-width on mobile, inline on desktop */}
          <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 mb-4">
            <button
              onClick={onCta}
              className="btn-primary text-base w-full sm:w-auto justify-center"
              data-testid="hero-cta-primary"
            >
              <Sparkles className="w-5 h-5" />
              {t.hero.cta_primary}
              <ArrowRight className="w-5 h-5" />
            </button>
            <a
              href="#how"
              onClick={scrollToHow}
              className="text-center sm:btn-outline text-sm sm:text-base text-[#57534E] sm:text-[#292524] underline sm:no-underline font-semibold py-2 sm:py-0"
              data-testid="hero-cta-secondary"
            >
              {t.hero.cta_secondary}
            </a>
          </div>

          {/* Microtrust — visible below CTA */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs sm:text-sm text-[#78716C]">
            <span className="inline-flex items-center gap-1.5" data-testid="hero-microtrust-free">
              <CircleDollarSign className="w-3.5 h-3.5 text-[#3B8268]" />
              <span className="font-semibold text-[#3B8268]">Free</span>
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="hero-microtrust-nocard">
              <Shield className="w-3.5 h-3.5 text-[#2A5C5A]" />
              No card required
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="hero-microtrust-time">
              <Clock className="w-3.5 h-3.5 text-[#D95D39]" />
              60 seconds
            </span>
          </div>
        </div>

        {/* RIGHT — CV Before/After compact mockup */}
        <div className="lg:col-span-5 relative order-2 lg:order-2 w-full">
          <div className="relative max-w-md mx-auto">
            {/* Before card (background, slightly rotated) */}
            <motion.div
              initial={{ opacity: 0, x: 20, rotate: 0 }}
              animate={{ opacity: 1, x: 0, rotate: -4 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="absolute -left-2 sm:-left-4 top-4 w-[85%] sm:w-[80%] bg-white border border-[#EAE5DB] rounded-2xl p-3 sm:p-4 shadow-lg opacity-70 pointer-events-none"
              aria-hidden="true"
              data-testid="hero-mockup-before"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-[#78716C]">BEFORE</div>
                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#D9423E]/10 text-[#D9423E] text-[9px] sm:text-[10px] font-bold">
                  <X className="w-2.5 h-2.5" />ATS 52 · REJECTED
                </div>
              </div>
              <div className="space-y-1">
                <div className="h-2 rounded-full bg-[#F2EBE1] w-3/4" />
                <div className="h-2 rounded-full bg-[#F2EBE1] w-1/2" />
                <div className="h-2 rounded-full bg-[#F2EBE1] w-2/3 mt-3" />
                <div className="h-2 rounded-full bg-[#F2EBE1] w-1/3" />
              </div>
            </motion.div>

            {/* After card (foreground) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative bg-white border-2 border-[#3B8268]/30 rounded-2xl p-4 sm:p-5 shadow-2xl ml-6 sm:ml-8 mt-1"
              data-testid="hero-mockup-after"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#3B8268]">AFTER — AussieCV</div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.4, delay: 1.0, type: "spring" }}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#3B8268] text-white text-[10px] sm:text-[11px] font-bold whitespace-nowrap"
                >
                  <Check className="w-2.5 h-2.5" />ATS 92 · READY
                </motion.div>
              </div>

              <div className="font-heading font-extrabold text-base sm:text-lg text-[#292524] leading-tight">JEAN DUPONT</div>
              <div className="text-[10px] sm:text-xs text-[#57534E] mb-2 leading-snug">
                FIFO Bus Driver · HR Licence · PTD Authorised<br />
                Karratha, WA 6714 · <span className="font-semibold">Visa 417 WHV</span>
              </div>

              <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-[#78716C] mb-1 mt-3 border-b border-[#F2EBE1] pb-1">Experience</div>
              <div className="text-[10px] sm:text-xs text-[#292524] font-semibold leading-snug">FIFO Bus Driver — Gold Rush Tour, Kalgoorlie WA</div>
              <div className="text-[9px] sm:text-[10px] text-[#78716C] italic">Jan 2024 – May 2025 (17 months)</div>

              <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-[#78716C] mb-1 mt-3 border-b border-[#F2EBE1] pb-1">Key Skills</div>
              <div className="flex flex-wrap gap-1">
                {["FIFO", "HR Licence", "PTD", "White Card", "Mine-site"].map((s) => (
                  <span key={s} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-[#F2EBE1] text-[9px] sm:text-[10px] font-semibold text-[#292524]">
                    {s}
                  </span>
                ))}
              </div>

              {/* ATS Score bar — animated */}
              <div className="mt-4 pt-3 border-t border-[#F2EBE1]">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-[#2A5C5A] flex items-center justify-center flex-shrink-0">
                    <Gauge className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-[#78716C]">ATS Score</div>
                    <div className="font-heading font-bold text-sm sm:text-base text-[#292524]">
                      52 <span className="text-[#D9423E]">→</span>{" "}
                      <span className="text-[#3B8268]">92</span>
                    </div>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-[#F2EBE1] overflow-hidden">
                  <motion.div
                    initial={{ width: "52%" }}
                    animate={{ width: "92%" }}
                    transition={{ delay: 0.8, duration: 1.4, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-[#E5A93D] to-[#3B8268]"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
